import React, { useEffect, useState, useCallback } from 'react';
import MainLayout from '../components/Layout/MainLayout.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.jsx';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, PieChart, Pie, Cell, ReferenceLine
} from 'recharts';
import { TrendingDown, TrendingUp, Users, Clock, GraduationCap, Leaf, Shield, Scale, DollarSign, Heart, Calendar, ArrowUp, ArrowDown, Minus, Target } from 'lucide-react';
import axios from 'axios';
import CompetitivenessIndex from '../components/CompetitivenessIndex.js';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const ML = { '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr', '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago', '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez' };
const PIE_COLORS = ['#2563eb', '#0f172a', '#3b82f6', '#64748b', '#1e40af', '#94a3b8', '#475569', '#60a5fa', '#1e293b', '#93c5fd'];
const fmt = (v) => typeof v === 'number' ? v.toLocaleString('pt-BR', { minimumFractionDigits: 0 }) : '--';
const fmtR = (v) => typeof v === 'number' ? `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 0 })}` : '--';

const Kpi = ({ label, value, suffix = '', icon: Icon, color = 'slate' }) => (
  <div className={`rounded-lg p-3 ${color === 'blue' ? 'bg-blue-50' : color === 'red' ? 'bg-red-50' : color === 'green' ? 'bg-emerald-50' : 'bg-slate-50'}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className={`w-3.5 h-3.5 ${color === 'blue' ? 'text-blue-500' : color === 'red' ? 'text-red-400' : color === 'green' ? 'text-emerald-500' : 'text-slate-400'}`} />
      <span className="text-[11px] text-slate-500">{label}</span>
    </div>
    <p className="text-xl font-bold text-slate-900">{value}{suffix}</p>
  </div>
);

const Section = ({ title, icon: Icon, children, id }) => (
  <div className="space-y-4" id={id} data-testid={`section-${id}`}>
    <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
      <Icon className="w-5 h-5 text-blue-600" />
      <h3 className="text-base font-semibold text-slate-900">{title}</h3>
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-slate-400 py-6 text-center">{text}</p>;

const PosicaoBadge = ({ posicao, diff }) => {
  if (posicao === 'sem_dados') return <span className="text-xs text-slate-400">--</span>;
  const cfg = {
    acima: { icon: ArrowUp, bg: 'bg-emerald-100 text-emerald-700', label: 'Acima' },
    abaixo: { icon: ArrowDown, bg: 'bg-red-100 text-red-700', label: 'Abaixo' },
    na_media: { icon: Minus, bg: 'bg-blue-100 text-blue-700', label: 'Na Media' },
  };
  const c = cfg[posicao] || cfg.na_media;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${c.bg}`} data-testid={`posicao-${posicao}`}>
      <Icon className="w-3 h-3" />
      {diff !== undefined ? `${diff > 0 ? '+' : ''}${diff}%` : c.label}
    </span>
  );
};

const PositionGauge = ({ value, p25, p50, p75, label }) => {
  if (!p50 || p50 === 0) return null;
  const maxVal = Math.max(value, p75) * 1.15;
  const minVal = Math.min(value, p25) * 0.85;
  const range = maxVal - minVal || 1;
  const pct = (v) => Math.max(0, Math.min(100, ((v - minVal) / range) * 100));

  return (
    <div className="space-y-1">
      <div className="text-[10px] text-slate-500 font-medium">{label}</div>
      <div className="relative h-6 bg-slate-100 rounded-full overflow-hidden">
        <div className="absolute top-0 h-full bg-gradient-to-r from-red-200 via-blue-100 to-emerald-200 rounded-full" style={{ left: `${pct(p25)}%`, width: `${pct(p75) - pct(p25)}%` }} />
        <div className="absolute top-1 h-4 w-0.5 bg-slate-400" style={{ left: `${pct(p25)}%` }} title={`P25: ${fmtR(p25)}`} />
        <div className="absolute top-0 h-6 w-0.5 bg-blue-600" style={{ left: `${pct(p50)}%` }} title={`P50: ${fmtR(p50)}`} />
        <div className="absolute top-1 h-4 w-0.5 bg-slate-400" style={{ left: `${pct(p75)}%` }} title={`P75: ${fmtR(p75)}`} />
        <div
          className="absolute top-0.5 w-5 h-5 rounded-full bg-slate-900 border-2 border-white shadow-md"
          style={{ left: `calc(${pct(value)}% - 10px)` }}
          title={`Sua empresa: ${fmtR(value)}`}
        />
      </div>
      <div className="flex justify-between text-[9px] text-slate-400">
        <span>P25: {fmtR(p25)}</span>
        <span className="font-medium text-blue-600">P50: {fmtR(p50)}</span>
        <span>P75: {fmtR(p75)}</span>
      </div>
    </div>
  );
};

const MinhaEmpresa = () => {
  const [ano, setAno] = useState('2024');
  const [hr, setHr] = useState(null);
  const [esg, setEsg] = useState(null);
  const [salary, setSalary] = useState(null);
  const [benefits, setBenefits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState('');
  const userRole = localStorage.getItem('userRole') || 'respondent';

  const loadAll = useCallback(async () => {
    setLoading(true);
    const h = getHeaders();
    try {
      const [hrRes, esgRes, salRes, benRes] = await Promise.all([
        axios.get(`${API}/my-indicators/hr?ano=${ano}`, { headers: h }),
        axios.get(`${API}/my-indicators/esg?ano=${ano}`, { headers: h }),
        axios.get(`${API}/my-indicators/salary?ano=${ano}`, { headers: h }),
        axios.get(`${API}/my-indicators/benefits?ano=${ano}`, { headers: h }),
      ]);
      setHr(hrRes.data);
      setEsg(esgRes.data);
      setSalary(salRes.data);
      setBenefits(benRes.data);
      setCompany(hrRes.data?.company || esgRes.data?.company || '');
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [ano]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const hrM = hr?.mensal || [];
  const esgM = esg?.mensal || [];
  const lastHr = hrM.length > 0 ? hrM[hrM.length - 1] : null;
  const lastEsg = esgM.length > 0 ? esgM[esgM.length - 1] : null;
  const cargos = salary?.cargos || [];
  const bens = benefits?.benefits || [];
  const nonZeroBens = bens.filter(b => b.valor > 0);
  const esgGeral = lastEsg ? ((lastEsg.score_ambiental || 0) + (lastEsg.score_social || 0) + (lastEsg.score_governanca || 0)) / 3 : 0;

  const salPos = salary?.posicionamento || {};
  const benPos = benefits?.posicionamento || {};

  const hrChart = hrM.map(m => ({ name: ML[m.mes] || m.mes, Turnover: m.turnover_geral_pct, Absenteismo: m.absenteismo_pct, 'T.Vol': m.turnover_voluntario_pct }));
  const hcChart = hrM.map(m => ({ name: ML[m.mes] || m.mes, Total: m.total_funcionarios, Fem: m.qtd_feminino, PCD: m.qtd_pcd }));
  const divChart = esgM.map(m => ({ name: ML[m.mes] || m.mes, Genero: m.diversidade_genero_pct, Racial: m.diversidade_racial_pct, PCD: m.diversidade_pcd_pct, 'Lid.Fem': m.lideranca_feminina_pct }));
  const radarData = lastEsg ? [
    { m: 'Ambiental', v: lastEsg.score_ambiental || 0 }, { m: 'Social', v: lastEsg.score_social || 0 },
    { m: 'Governanca', v: lastEsg.score_governanca || 0 }, { m: 'Diversidade', v: lastEsg.diversidade_genero_pct || 0 },
    { m: 'Lid.Fem.', v: lastEsg.lideranca_feminina_pct || 0 }, { m: 'Equidade', v: Math.max(0, 100 - (lastEsg.equidade_salarial_gap_pct || 0)) },
  ] : [];

  const salCompChart = cargos.filter(c => c.mercado_p50 > 0).map(c => ({
    name: c.cargo.length > 18 ? c.cargo.slice(0, 18) + '..' : c.cargo,
    Empresa: c.salario_base,
    Mercado: c.mercado_p50,
  }));

  const benCompChart = nonZeroBens.filter(b => b.mercado_p50 > 0).map(b => ({
    name: b.label.length > 13 ? b.label.slice(0, 13) + '..' : b.label,
    Empresa: b.valor,
    Mercado: b.mercado_p50,
  }));

  if (loading) return <MainLayout title="Minha Empresa" userRole={userRole}><div className="flex items-center justify-center py-20 text-slate-400">Carregando...</div></MainLayout>;

  return (
    <MainLayout title="Minha Empresa" userRole={userRole}>
      <div className="space-y-8" data-testid="minha-empresa-page">
        {/* Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-900 to-blue-900 rounded-xl p-5 text-white">
          <div>
            <p className="text-sm opacity-60">Visao consolidada</p>
            <h2 className="text-xl font-bold mt-0.5">{company || 'Minha Empresa'}</h2>
            <p className="text-xs opacity-40 mt-1">Indicadores RH + ESG + Remuneração vs Mercado + Benefícios vs Mercado</p>
          </div>
          <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
            <Calendar className="w-4 h-4 opacity-60" />
            <Select value={ano} onValueChange={setAno}>
              <SelectTrigger className="w-[90px] h-8 text-sm bg-transparent border-0 text-white focus:ring-0" data-testid="global-year-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2023, 2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Índice de Competitividade */}
        <CompetitivenessIndex variant="full" ano={parseInt(ano)} />

        {/* Quick nav */}
        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'rh', label: 'Indicadores RH', count: hrM.length + ' meses' },
            { id: 'esg', label: 'ESG & Pessoas', count: esgM.length + ' meses' },
            { id: 'remuneracao', label: 'Remuneracao', count: cargos.length + ' cargos' },
            { id: 'beneficios', label: 'Beneficios', count: nonZeroBens.length + ' itens' },
          ].map(s => (
            <a key={s.id} href={`#${s.id}`} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm text-slate-700 transition-colors" data-testid={`nav-${s.id}`}>
              {s.label} <span className="text-xs text-slate-400 ml-1">({s.count})</span>
            </a>
          ))}
        </div>

        {/* ===== RH ===== */}
        <Section title="Indicadores de RH" icon={Users} id="rh">
          {hrM.length === 0 ? <Empty text={`Nenhum dado de RH para ${ano}. Responda as pesquisas para visualizar.`} /> : (
            <>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                <Kpi label="Funcionarios" value={lastHr?.total_funcionarios || 0} icon={Users} color="blue" />
                <Kpi label="Turnover" value={(lastHr?.turnover_geral_pct || 0).toFixed(1)} suffix="%" icon={TrendingDown} color="red" />
                <Kpi label="Absenteismo" value={(lastHr?.absenteismo_pct || 0).toFixed(1)} suffix="%" icon={Clock} color="red" />
                <Kpi label="Feminino" value={(lastHr?.headcount_feminino_pct || 0).toFixed(1)} suffix="%" icon={Users} color="green" />
                <Kpi label="PCD" value={(lastHr?.pcd_pct || 0).toFixed(1)} suffix="%" icon={Users} color={lastHr?.pcd_pct >= 5 ? 'green' : 'red'} />
                <Kpi label="Treinamento" value={(lastHr?.treinamento_horas_per_capita || 0).toFixed(1)} suffix=" h" icon={GraduationCap} color="blue" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Evolução Turnover & Absenteísmo</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={hrChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} unit="%" />
                        <Tooltip formatter={v => `${v?.toFixed(2)}%`} />
                        <Line type="monotone" dataKey="Turnover" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="T.Vol" stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 4" dot={{ r: 2 }} />
                        <Line type="monotone" dataKey="Absenteismo" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Headcount Mensal</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={hcChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Bar dataKey="Total" fill="#2563eb" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="Fem" fill="#64748b" radius={[2, 2, 0, 0]} />
                        <Bar dataKey="PCD" fill="#0f172a" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Detalhamento Mensal</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="hr-table">
                      <thead><tr className="border-b border-slate-200 text-left">
                        <th className="py-1.5 px-2 font-medium text-slate-500">Mes</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Func.</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Adm.</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Desl.</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Turn.%</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Abs.%</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Fem.%</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">PCD%</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Trein.h</th>
                      </tr></thead>
                      <tbody>{hrM.map((m, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-1.5 px-2 font-medium">{ML[m.mes] || m.mes}</td>
                          <td className="py-1.5 px-2 text-right">{m.total_funcionarios}</td>
                          <td className="py-1.5 px-2 text-right text-emerald-600">{m.admissoes}</td>
                          <td className="py-1.5 px-2 text-right text-red-500">{m.demissoes_total}</td>
                          <td className="py-1.5 px-2 text-right font-medium">{m.turnover_geral_pct?.toFixed(2)}%</td>
                          <td className="py-1.5 px-2 text-right">{m.absenteismo_pct?.toFixed(2)}%</td>
                          <td className="py-1.5 px-2 text-right">{m.headcount_feminino_pct?.toFixed(1)}%</td>
                          <td className={`py-1.5 px-2 text-right ${m.pcd_pct >= 5 ? 'text-emerald-600' : 'text-red-500'}`}>{m.pcd_pct?.toFixed(1)}%</td>
                          <td className="py-1.5 px-2 text-right">{m.treinamento_horas_per_capita?.toFixed(1)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </Section>

        {/* ===== ESG ===== */}
        <Section title="ESG & Pessoas" icon={Leaf} id="esg">
          {esgM.length === 0 ? <Empty text={`Nenhum dado ESG para ${ano}.`} /> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Score ESG', value: esgGeral, icon: Shield, color: 'blue' },
                  { label: 'Ambiental', value: lastEsg?.score_ambiental, icon: Leaf, color: 'green' },
                  { label: 'Social', value: lastEsg?.score_social, icon: Users, color: 'blue' },
                  { label: 'Governanca', value: lastEsg?.score_governanca, icon: Scale, color: 'slate' },
                ].map((s, i) => (
                  <div key={i} className={`rounded-lg p-4 ${s.color === 'blue' ? 'bg-blue-50' : s.color === 'green' ? 'bg-emerald-50' : 'bg-slate-50'}`} data-testid={`esg-score-${i}`}>
                    <div className="flex items-center gap-1.5 mb-1"><s.icon className="w-3.5 h-3.5 opacity-50" /><span className="text-[11px] opacity-60">{s.label}</span></div>
                    <p className="text-2xl font-bold text-slate-900">{s.value?.toFixed(1) || '--'}</p>
                    <p className="text-[10px] opacity-40">/ 100</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Evolução Diversidade</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={divChart}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} unit="%" />
                        <Tooltip formatter={v => `${v?.toFixed(1)}%`} />
                        <Line type="monotone" dataKey="Genero" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="Racial" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="PCD" stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="Lid.Fem" stroke="#64748b" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Radar ESG (Último mês)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="#e2e8f0" />
                        <PolarAngleAxis dataKey="m" tick={{ fontSize: 10 }} />
                        <PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0, 100]} />
                        <Radar dataKey="v" stroke="#2563eb" fill="#2563eb" fillOpacity={0.15} strokeWidth={2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Diversidade (Último mês)</CardTitle></CardHeader>
                  <CardContent className="text-sm space-y-1.5">
                    {[
                      { l: 'Mulheres no quadro', v: lastEsg?.diversidade_genero_pct },
                      { l: 'Diversidade racial', v: lastEsg?.diversidade_racial_pct },
                      { l: 'PCD', v: lastEsg?.diversidade_pcd_pct },
                      { l: 'Lideranca feminina', v: lastEsg?.lideranca_feminina_pct },
                      { l: 'Gap salarial genero', v: lastEsg?.equidade_salarial_gap_pct },
                    ].map((r, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-slate-50">
                        <span className="text-slate-600">{r.l}</span>
                        <span className="font-semibold text-slate-900">{r.v !== undefined ? `${r.v.toFixed(1)}%` : '--'}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Ambiental (Último mês)</CardTitle></CardHeader>
                  <CardContent className="text-sm space-y-1.5">
                    {[
                      { l: 'Emissoes CO2', v: lastEsg?.emissoes_co2_toneladas, u: ' ton' },
                      { l: 'Energia renovavel', v: lastEsg?.energia_renovavel_pct, u: '%' },
                      { l: 'Residuos reciclados', v: lastEsg?.residuos_reciclados_pct, u: '%' },
                    ].map((r, i) => (
                      <div key={i} className="flex justify-between py-1 border-b border-slate-50">
                        <span className="text-slate-600">{r.l}</span>
                        <span className="font-semibold text-slate-900">{r.v !== undefined ? `${r.v.toFixed(1)}${r.u}` : '--'}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </Section>

        {/* ===== REMUNERACAO vs MERCADO ===== */}
        <Section title="Remuneração vs Mercado" icon={DollarSign} id="remuneracao">
          {cargos.length === 0 ? <Empty text={`Nenhum dado de remuneracao para ${ano}.`} /> : (
            <>
              {/* Posicionamento Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-slate-900 rounded-lg p-4 text-white">
                  <p className="text-[11px] opacity-50">Media Base</p>
                  <p className="text-xl font-bold">{fmtR(salary?.media_salario_base)}</p>
                </div>
                <div className="bg-emerald-600 rounded-lg p-4 text-white" data-testid="salary-pos-acima">
                  <div className="flex items-center gap-1.5"><ArrowUp className="w-4 h-4 opacity-70" /><p className="text-[11px] opacity-70">Acima do Mercado</p></div>
                  <p className="text-2xl font-bold">{salPos.acima || 0} <span className="text-sm font-normal opacity-60">cargos ({salPos.pct_acima || 0}%)</span></p>
                </div>
                <div className="bg-blue-600 rounded-lg p-4 text-white" data-testid="salary-pos-media">
                  <div className="flex items-center gap-1.5"><Minus className="w-4 h-4 opacity-70" /><p className="text-[11px] opacity-70">Na Media</p></div>
                  <p className="text-2xl font-bold">{salPos.na_media || 0} <span className="text-sm font-normal opacity-60">cargos ({salPos.pct_na_media || 0}%)</span></p>
                </div>
                <div className="bg-red-500 rounded-lg p-4 text-white" data-testid="salary-pos-abaixo">
                  <div className="flex items-center gap-1.5"><ArrowDown className="w-4 h-4 opacity-70" /><p className="text-[11px] opacity-70">Abaixo do Mercado</p></div>
                  <p className="text-2xl font-bold">{salPos.abaixo || 0} <span className="text-sm font-normal opacity-60">cargos ({salPos.pct_abaixo || 0}%)</span></p>
                </div>
              </div>

              {/* Comparative chart: Empresa vs Mercado P50 */}
              {salCompChart.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Empresa vs Mercado (Mediana P50)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(220, salCompChart.length * 40)}>
                      <BarChart data={salCompChart} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 120 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={115} />
                        <Tooltip formatter={v => fmtR(v)} />
                        <Bar dataKey="Empresa" fill="#0f172a" radius={[0, 3, 3, 0]} barSize={14} />
                        <Bar dataKey="Mercado" fill="#3b82f6" radius={[0, 3, 3, 0]} barSize={14} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Gauge per cargo (top 5) */}
              {cargos.filter(c => c.mercado_p50 > 0).length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Posição no Mercado por Cargo</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    {cargos.filter(c => c.mercado_p50 > 0).slice(0, 8).map((c, i) => (
                      <div key={i} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-700">{c.cargo}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{fmtR(c.salario_base)}</span>
                            <PosicaoBadge posicao={c.posicao} diff={c.diff_mercado_pct} />
                          </div>
                        </div>
                        <PositionGauge value={c.salario_base} p25={c.mercado_p25} p50={c.mercado_p50} p75={c.mercado_p75} />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Detail table */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Tabela Detalhada com Posição</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="salary-market-table">
                      <thead><tr className="border-b border-slate-200 text-left">
                        <th className="py-1.5 px-2 font-medium text-slate-500">Cargo</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Sua Empresa</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Mercado P50</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Mercado P75</th>
                        <th className="py-1.5 px-2 text-right font-medium text-slate-500">Diferenca</th>
                        <th className="py-1.5 px-2 text-center font-medium text-slate-500">Posicao</th>
                      </tr></thead>
                      <tbody>{cargos.map((c, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-1.5 px-2 font-medium text-slate-900">{c.cargo}</td>
                          <td className="py-1.5 px-2 text-right">{fmtR(c.salario_base)}</td>
                          <td className="py-1.5 px-2 text-right text-blue-600">{c.mercado_p50 ? fmtR(c.mercado_p50) : '--'}</td>
                          <td className="py-1.5 px-2 text-right text-slate-500">{c.mercado_p75 ? fmtR(c.mercado_p75) : '--'}</td>
                          <td className={`py-1.5 px-2 text-right font-medium ${c.diff_mercado_pct > 0 ? 'text-emerald-600' : c.diff_mercado_pct < 0 ? 'text-red-500' : 'text-slate-500'}`}>
                            {c.diff_mercado_pct !== 0 ? `${c.diff_mercado_pct > 0 ? '+' : ''}${c.diff_mercado_pct}%` : '--'}
                          </td>
                          <td className="py-1.5 px-2 text-center"><PosicaoBadge posicao={c.posicao} /></td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </Section>

        {/* ===== BENEFICIOS vs MERCADO ===== */}
        <Section title="Benefícios vs Mercado" icon={Heart} id="beneficios">
          {bens.length === 0 ? <Empty text={`Nenhum dado de beneficios para ${ano}.`} /> : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-lg p-4 text-white">
                  <p className="text-xs opacity-50">Total Mensal</p>
                  <p className="text-2xl font-bold mt-1">{fmtR(benefits?.total_beneficios)}</p>
                </div>
                <div className="bg-emerald-600 rounded-lg p-4 text-white" data-testid="ben-pos-acima">
                  <div className="flex items-center gap-1.5"><ArrowUp className="w-4 h-4 opacity-70" /><p className="text-[11px] opacity-70">Acima do Mercado</p></div>
                  <p className="text-2xl font-bold">{benPos.acima || 0} <span className="text-sm font-normal opacity-60">({benPos.pct_acima || 0}%)</span></p>
                </div>
                <div className="bg-blue-600 rounded-lg p-4 text-white" data-testid="ben-pos-media">
                  <div className="flex items-center gap-1.5"><Minus className="w-4 h-4 opacity-70" /><p className="text-[11px] opacity-70">Na Media</p></div>
                  <p className="text-2xl font-bold">{benPos.na_media || 0} <span className="text-sm font-normal opacity-60">({benPos.pct_na_media || 0}%)</span></p>
                </div>
                <div className="bg-red-500 rounded-lg p-4 text-white" data-testid="ben-pos-abaixo">
                  <div className="flex items-center gap-1.5"><ArrowDown className="w-4 h-4 opacity-70" /><p className="text-[11px] opacity-70">Abaixo do Mercado</p></div>
                  <p className="text-2xl font-bold">{benPos.abaixo || 0} <span className="text-sm font-normal opacity-60">({benPos.pct_abaixo || 0}%)</span></p>
                </div>
              </div>

              {/* Benefits comparison chart */}
              {benCompChart.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Empresa vs Mercado (Mediana P50)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(220, benCompChart.length * 36)}>
                      <BarChart data={benCompChart} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 100 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={v => `R$${v}`} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={95} />
                        <Tooltip formatter={v => fmtR(v)} />
                        <Bar dataKey="Empresa" fill="#0f172a" radius={[0, 3, 3, 0]} barSize={14} />
                        <Bar dataKey="Mercado" fill="#3b82f6" radius={[0, 3, 3, 0]} barSize={14} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Benefit items with market gauge */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                {bens.map((b, i) => (
                  <div key={i} className={`border rounded-lg p-3 transition-colors ${b.posicao === 'acima' ? 'border-emerald-200 bg-emerald-50/50' : b.posicao === 'abaixo' ? 'border-red-200 bg-red-50/50' : b.posicao === 'na_media' ? 'border-blue-200 bg-blue-50/50' : 'border-slate-200 bg-white'}`} data-testid={`ben-card-${b.key}`}>
                    <p className="text-[11px] text-slate-500 truncate">{b.label}</p>
                    <p className="text-base font-bold text-slate-900 mt-0.5">{b.valor > 0 ? fmtR(b.valor) : '--'}</p>
                    {b.mercado_p50 > 0 && (
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="text-[10px] text-slate-400">P50: {fmtR(b.mercado_p50)}</span>
                        <PosicaoBadge posicao={b.posicao} diff={b.diff_mercado_pct} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pie distribution */}
              {nonZeroBens.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Distribuição dos Benefícios</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={nonZeroBens.map(b => ({ name: b.label, value: b.valor }))} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: R$${value}`} labelLine={{ strokeWidth: 0.5 }}>
                          {nonZeroBens.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => fmtR(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </Section>
      </div>
    </MainLayout>
  );
};

export default MinhaEmpresa;
